import { useEffect, useId, useMemo, useRef, useState } from "react";
import { neighborhoodMatchesQuery } from "../../../utils/casamap/casaMapaNeighborhood";

function toSelected(value, values, multiple) {
  if (multiple) {
    return (Array.isArray(values) ? values : []).map((item) => String(item || "").trim()).filter(Boolean);
  }
  return value ? [String(value)] : [];
}

export default function CasaMapaAdminFilterCombobox({
  label,
  options = [],
  value = "",
  values,
  onChange,
  multiple = false,
  allLabel,
  placeholder,
  clearLabel,
  noResultsLabel,
  selectedCountLabel,
}) {
  const inputId = useId();
  const listboxId = useId();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(0);
  const selected = toSelected(value, values, multiple);
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const filtered = useMemo(() => {
    const rows = Array.isArray(options) ? options.filter(Boolean) : [];
    const term = query.trim();
    if (!term) return rows;
    return rows.filter((name) => neighborhoodMatchesQuery(name, term));
  }, [options, query]);

  const items = useMemo(() => [null, ...filtered], [filtered]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [query, open]);

  const emit = (next) => {
    if (multiple) {
      onChange?.(next);
      return;
    }
    onChange?.(next[0] || "");
  };

  const clearAll = () => {
    emit([]);
    setQuery("");
    if (!multiple) setOpen(false);
  };

  const toggleValue = (name) => {
    if (!name) {
      clearAll();
      return;
    }
    if (!multiple) {
      emit([name]);
      setOpen(false);
      setQuery("");
      return;
    }
    if (selectedSet.has(name)) {
      emit(selected.filter((item) => item !== name));
      return;
    }
    emit([...selected, name]);
  };

  const onKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setHighlightIndex((current) => Math.min(current + 1, Math.max(items.length - 1, 0)));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) return;
      setHighlightIndex((current) => Math.max(current - 1, 0));
      return;
    }
    if ((event.key === "Enter" || event.key === " ") && open) {
      event.preventDefault();
      toggleValue(items[highlightIndex] || "");
      return;
    }
    if (event.key === "Backspace" && multiple && !query && selected.length) {
      event.preventDefault();
      emit(selected.slice(0, -1));
      return;
    }
    if (event.key === "Escape") {
      setOpen(false);
      setQuery("");
    }
  };

  const summaryLabel =
    selected.length === 0
      ? allLabel
      : selected.length === 1
        ? selected[0]
        : selectedCountLabel
          ? selectedCountLabel({ count: selected.length })
          : selected.join(", ");

  return (
    <div className={`cm-trend-combobox${multiple ? " cm-trend-combobox--multiple" : ""}`} ref={rootRef}>
      <label className="cm-trend-filters__field" htmlFor={inputId}>
        <span>{label}</span>
        <div className="cm-trend-combobox__control">
          {multiple && selected.length
            ? selected.map((name) => (
                <button
                  key={name}
                  type="button"
                  className="cm-trend-combobox__chip"
                  onClick={(event) => {
                    event.preventDefault();
                    toggleValue(name);
                  }}
                >
                  <span>{name}</span>
                  <span aria-hidden="true">×</span>
                </button>
              ))
            : null}
          <input
            id={inputId}
            type="text"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-multiselectable={multiple || undefined}
            placeholder={
              open || (multiple && selected.length) ? placeholder || allLabel : summaryLabel
            }
            value={open || multiple ? query : selected[0] || ""}
            onChange={(event) => {
              setQuery(event.target.value);
              if (!open) setOpen(true);
            }}
            onFocus={() => {
              setOpen(true);
              setQuery("");
            }}
            onKeyDown={onKeyDown}
          />
          {selected.length ? (
            <button
              type="button"
              className="cm-trend-combobox__clear"
              onClick={clearAll}
            >
              {clearLabel}
            </button>
          ) : null}
          <span className="cm-trend-combobox__chevron" aria-hidden="true" />
        </div>
      </label>
      {open ? (
        <ul
          id={listboxId}
          className="cm-trend-combobox__list"
          role="listbox"
          aria-multiselectable={multiple || undefined}
        >
          <li
            role="option"
            aria-selected={selected.length === 0}
            className={`cm-trend-combobox__option${highlightIndex === 0 ? " is-active" : ""}${
              selected.length === 0 ? " is-selected" : ""
            }`}
            onMouseEnter={() => setHighlightIndex(0)}
            onMouseDown={(event) => {
              event.preventDefault();
              toggleValue("");
            }}
          >
            {multiple ? (
              <span className="cm-trend-combobox__check" aria-hidden="true">
                {selected.length === 0 ? "☑" : "☐"}
              </span>
            ) : null}
            {allLabel}
          </li>
          {filtered.map((name, index) => {
            const optionIndex = index + 1;
            const isActive = highlightIndex === optionIndex;
            const isSelected = selectedSet.has(name);
            return (
              <li
                key={name}
                role="option"
                aria-selected={isSelected}
                className={`cm-trend-combobox__option${isActive ? " is-active" : ""}${
                  isSelected ? " is-selected" : ""
                }`}
                onMouseEnter={() => setHighlightIndex(optionIndex)}
                onMouseDown={(event) => {
                  event.preventDefault();
                  toggleValue(name);
                }}
              >
                {multiple ? (
                  <span className="cm-trend-combobox__check" aria-hidden="true">
                    {isSelected ? "☑" : "☐"}
                  </span>
                ) : null}
                {name}
              </li>
            );
          })}
          {!filtered.length ? (
            <li className="cm-trend-combobox__empty" role="presentation">
              {noResultsLabel}
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
